import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import projectService from '../../../../services/projectService';
import RightDrawer from '../../../../shared/components/form/RightDrawer';
import PageBreadcrumb from '../../../../shared/PageBreadCrumb';
import ProjectTable from '../../components/ProjectTable';
import ProjectAssetDrawer from '../../components/ProjectAssetDrawer';
import EquipmentTasksDrawer from '../../components/EquipmentTasksDrawer';
import ProjectAssetManagement from '../../components/ProjectAssetManagement';
import ProjectProgressHeader from '../../components/ProjectProgressHeader';
import ProjectBulkUploadModal from '../../components/ProjectBulkUploadModal';
import projectAssetService from '../../../../services/projectAssetService';
import {
  isProjectInfoComplete,
  getTotalAssetCount,
  getTotalEquipmentCount,
  getTotalResourceCount,
  getNormalizedApprovalStatus,
} from '../../utils/projectStatusUtils';
import { safeCreateDate } from '../../../../utils/dateUtils';

const ITEMS_PER_PAGE = 12;
// Statuses below are computed from project assets/equipment/resources after fetch,
// so they require client-side filtering. We narrow the candidate set on the backend
// via approvalStatus where possible, then paginate locally to keep totals accurate.
const CLIENT_FILTER_STATUSES = new Set(['draft', 'ready', 'started']);
const CLIENT_FILTER_FETCH_LIMIT = 1000;
// Backend approvalStatus values that are eligible candidates for each computed pill.
// These narrow the fetch so we don't pull approved projects when looking for drafts, etc.
const APPROVAL_STATUS_CANDIDATES: Record<string, string[]> = {
  draft: ['draft', 'not_required', 'rejected'],
  ready: ['draft', 'not_required'],
  // 'started' is handled by backend `projectStatus=started` (approved + not_required)
};

const hasProjectStarted = (project: any): boolean => {
  const raw =
    project?.actualStartDate ||
    project?.startDate ||
    project?.plannedStartDate ||
    project?.projectStartDate ||
    project?.expectedStartDate;
  const start = safeCreateDate(raw);
  if (!start) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const normalized = new Date(start);
  normalized.setHours(0, 0, 0, 0);
  return normalized.getTime() <= today.getTime();
};

export default function Projects() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState<any[]>([]);
  const [totalProjects, setTotalProjects] = useState<number>(0);
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [search, setSearch] = useState<string>("");
  const [projectTypeId, setProjectTypeId] = useState<string>("");
  const [location, setLocation] = useState<string>("");
  const [approvalStatus, setApprovalStatus] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [sortField, setSortField] = useState<string>("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [openCreateEquipment, setOpenCreateEquipment] = useState(false);
  const [openEquipmentDetails, setOpenEquipmentDetails] = useState(false);
  const [activeEquipmentId] = useState<string | null>(null);
  const [openProjectAsset, setOpenProjectAsset] = useState(false);
  const [openEquipmentTasks, setOpenEquipmentTasks] = useState(false);
  const [openProjectAssetsDrawer, setOpenProjectAssetsDrawer] = useState(false);
  const [openBulkUploadDrawer, setOpenBulkUploadDrawer] = useState(false);
  const [progressRefreshKey, setProgressRefreshKey] = useState(0);

  // Cache for client-filtered candidate list. Keyed by the filter signature so paging
  // through results doesn't refetch the whole batch on each page click.
  const candidateCacheRef = useRef<{ key: string; items: any[] } | null>(null);

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Statuses like draft/ready/started are computed from per-project asset/equipment/resource
      // counts. The backend can narrow on approvalStatus, so we pre-filter there and then
      // refine + paginate locally to keep totals and pagination accurate.
      const requiresClientFilter = CLIENT_FILTER_STATUSES.has(approvalStatus);

      // Draft: no assets attached.
      // Ready: has assets+equipment+resources, in draft/not_required, not yet submitted.
      // Started: approved/not_required, has assets, NOT in ready-to-send state, AND start date has passed.
      const isReadyToSend = (p: any) => {
        const status = getNormalizedApprovalStatus(p);
        const canSend = status === 'draft' || status === 'not_required' || !status;
        return (
          getTotalAssetCount(p) > 0 &&
          getTotalEquipmentCount(p) > 0 &&
          getTotalResourceCount(p) > 0 &&
          canSend
        );
      };

      const applyClientFilter = (list: any[]) => {
        if (approvalStatus === 'draft') {
          return list.filter((p: any) => getTotalAssetCount(p) === 0);
        }
        if (approvalStatus === 'ready') {
          return list.filter((p: any) => isReadyToSend(p));
        }
        return list.filter(
          (p: any) =>
            getTotalAssetCount(p) > 0 &&
            !isReadyToSend(p) &&
            hasProjectStarted(p)
        );
      };

      const cacheKey = JSON.stringify({
        search,
        projectTypeId,
        location,
        approvalStatus,
        sortField,
        sortOrder,
      });

      // For paginated client-filter results, reuse the cached candidate list when only
      // `page` changed. This avoids refetching 1000 records on each pagination click.
      if (requiresClientFilter && candidateCacheRef.current?.key === cacheKey) {
        const filteredAll = candidateCacheRef.current.items;
        const totalFiltered = filteredAll.length;
        const totalPages = Math.max(1, Math.ceil(totalFiltered / ITEMS_PER_PAGE));
        const safePage = Math.min(Math.max(1, page), totalPages);
        const startIdx = (safePage - 1) * ITEMS_PER_PAGE;
        setProjects(filteredAll.slice(startIdx, startIdx + ITEMS_PER_PAGE));
        setTotalProjects(totalFiltered);
        if (safePage !== page) setPage(safePage);
        setLoading(false);
        return;
      }

      const params: any = {
        page: requiresClientFilter ? 1 : page,
        limit: requiresClientFilter ? CLIENT_FILTER_FETCH_LIMIT : ITEMS_PER_PAGE,
        sortBy: sortField,
        sortOrder: sortOrder,
      };

      if (search) params.projectName = search;
      if (projectTypeId) params.projectTypeId = projectTypeId;
      if (location) params.locationId = location;

      if (approvalStatus === 'started') {
        params.projectStatus = 'started';
      } else if (approvalStatus && APPROVAL_STATUS_CANDIDATES[approvalStatus]) {
        // Pre-narrow the candidate set so we don't pull every project to find drafts/ready ones.
        params.approvalStatus = APPROVAL_STATUS_CANDIDATES[approvalStatus].join(',');
      } else if (approvalStatus && !requiresClientFilter) {
        params.approvalStatus = approvalStatus;
      }

      const result = await projectService.getProjects(params);
      const list = result.data || [];

      // Keep selected project in sync so the drawer shows the latest approvalStatus after
      // submit/resubmit, regardless of whether it's part of the current visible page.
      setSelectedProject((prev: any) => {
        if (!prev?._id && !prev?.id) return prev;
        const id = prev._id || prev.id;
        const updated = list.find((p: any) => (p._id || p.id) === id);
        return updated ?? prev;
      });

      if (!requiresClientFilter) {
        candidateCacheRef.current = null;
        setProjects(list);
        setTotalProjects(result.meta?.totalDocs || 0);
        return;
      }

      const filteredAll = applyClientFilter(list);
      candidateCacheRef.current = { key: cacheKey, items: filteredAll };

      const totalFiltered = filteredAll.length;
      const totalPages = Math.max(1, Math.ceil(totalFiltered / ITEMS_PER_PAGE));
      const safePage = Math.min(Math.max(1, page), totalPages);
      const startIdx = (safePage - 1) * ITEMS_PER_PAGE;

      setProjects(filteredAll.slice(startIdx, startIdx + ITEMS_PER_PAGE));
      setTotalProjects(totalFiltered);

      if (safePage !== page) setPage(safePage);
    } catch (err: any) {
      console.error("Failed to fetch projects", err);
      setError(err.message || "Failed to fetch projects");
      setProjects([]);
      setTotalProjects(0);
    } finally {
      setLoading(false);
    }
  }, [search, projectTypeId, location, approvalStatus, page, sortField, sortOrder]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Force-refresh: invalidate cache so the next fetch hits the network even when
  // the filter signature is unchanged (e.g. after submit/approve/reject mutations).
  const refreshProjects = useCallback(async () => {
    candidateCacheRef.current = null;
    await fetchProjects();
  }, [fetchProjects]);

  // Handle URL parameters for auto-selecting project and showing asset equipment
  useEffect(() => {
    const selectedProjectId = searchParams.get('selectedProject');

    if (selectedProjectId && projects.length > 0) {
      // Find and auto-select the project
      const project = projects.find(p => p._id === selectedProjectId || p.id === selectedProjectId);
      if (project && (!selectedProject || selectedProject._id !== selectedProjectId)) {
        setSelectedProject(project);

        // Clear the URL parameters
        setSearchParams(prev => {
          const newParams = new URLSearchParams(prev);
          newParams.delete('selectedProject');
          // Keep showAssetEquipment for the ProjectAssetManagement component to handle
          return newParams;
        });
      }
    }
  }, [projects, searchParams, setSearchParams, selectedProject]);

  const handleAdd = () => {
    navigate('/projects/ai-create');
  };

  const handleAddClassic = () => {
    navigate('/projects/create');
  };

  const handleBulkUpload = () => {
    setOpenBulkUploadDrawer(true);
  };

  const handleEdit = (project: any) => {
    navigate(`/projects/edit/${project._id || project.id}`);
  };

  const handleSelect = async (project: any) => {
    console.log('Selecting project:', project);
    console.log('Project ID:', project._id || project.id);
    setSelectedProject(project);

    // Only auto-open drawer if project has 0 assets (first time setup)
    try {
      const projectId = project._id || project.id;
      const assets = await projectAssetService.getProjectAssetsByProject(projectId);
      const assetCount = assets?.length || 0;

      // Only open drawer automatically if project has no assets (first time)
      if (assetCount === 0) {
        setOpenProjectAssetsDrawer(true);
      }
      // If project has assets, don't auto-open - user can manually open if needed
    } catch (error) {
      console.error('Failed to check asset count:', error);
      // On error, don't auto-open to be safe
    }
  };

  const handleOpenAssetDrawer = (project: any) => {
    if (!isProjectInfoComplete(project)) {
      const id = project._id || project.id;
      if (id) navigate(`/projects/edit/${id}?completeSetup=1`);
      return;
    }
    setSelectedProject(project);
    setOpenProjectAssetsDrawer(true);
  };

  const handleOpenCalendar = (project: any) => {
    const id = project._id || project.id;
    if (id) navigate(`/projects/${id}/calendar`);
  };



  const triggerProgressRefresh = useCallback(() => {
    setProgressRefreshKey((prev) => prev + 1);
  }, []);

  return (
    <div className="max-w-9xl mx-auto space-y-6 relative">
      {/* Page Header */}
      <PageBreadcrumb pageTitle="Project Management" />

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      {/* Project Table - Full Width */}
      <ProjectTable
        projects={projects}
        search={search}
        setSearch={setSearch}
        projectTypeId={projectTypeId}
        setProjectTypeId={setProjectTypeId}
        location={location}
        setLocation={setLocation}
        approvalStatus={approvalStatus}
        setApprovalStatus={setApprovalStatus}
        page={page}
        setPage={setPage}
        totalProjects={totalProjects}
        onEdit={handleEdit}
        onAdd={handleAddClassic}
        onAddWithAI={handleAdd}
        addButtonLabel="Create project"
        aiCreateLinkLabel="Create with AI Architect"
        onSelect={handleSelect}
        onOpenAssetDrawer={handleOpenAssetDrawer}
        onOpenCalendar={handleOpenCalendar}
        selectedProject={selectedProject}
        onRefresh={refreshProjects}
        sortField={sortField}
        sortOrder={sortOrder}
        setSortField={setSortField}
        setSortOrder={setSortOrder}
        loading={loading}
      />

      {/* Create Equipment Drawer */}
      <RightDrawer
        open={openCreateEquipment}
        title="Create Equipment"
        onClose={() => setOpenCreateEquipment(false)}
        size="3xl"
      >
        {selectedProject && (
          // @ts-ignore - lazy loaded component type
          <EquipmentFormWrapper
            projectId={(selectedProject as any)._id || (selectedProject as any).id}
            onCreated={() => { setOpenCreateEquipment(false); void handleSelect(selectedProject); }}
          />
        )}
      </RightDrawer>

      {/* Equipment Details Drawer (Handled within ProjectAssetManagement/TreeView) */}

      {/* Project Asset Drawer (for managing asset checkpoints) */}
      <ProjectAssetDrawer
        open={openProjectAsset}
        onClose={() => setOpenProjectAsset(false)}
        assetName=""
        projectId={selectedProject?._id || selectedProject?.id || ''}
        masterAssetId=""
      />

      {/* Equipment Tasks Drawer (for viewing equipment tasks only) */}
      <EquipmentTasksDrawer
        open={openEquipmentTasks}
        onClose={() => setOpenEquipmentTasks(false)}
        equipment={null}
        projectStart={selectedProject?.startDate || selectedProject?.dateCreated}
        projectEnd={selectedProject?.endDate || selectedProject?.lastmodifiedDate}
      />

      {/* Project Assets Drawer - Full Width */}
      <RightDrawer
        open={openProjectAssetsDrawer}
        title={selectedProject ? `${selectedProject.projectName || selectedProject.name} - Project Assets` : 'Project Assets'}
        onClose={() => {
          setOpenProjectAssetsDrawer(false);
          setSelectedProject(null);
        }}
        size="full"
      >
        <div className="p-0">
          {selectedProject && (
            <>
              {/* Progress Header */}
              <div className="border-b border-gray-200">
                <ProjectProgressHeader
                  project={selectedProject}
                  refreshSignal={progressRefreshKey}
                  onRefresh={async () => {
                    await refreshProjects();
                    const id = selectedProject?._id || selectedProject?.id;
                    if (id) {
                      try {
                        const updated = await projectService.getProjectById(id);
                        setSelectedProject(updated);
                      } catch {
                        // Keep current selection if refetch fails
                      }
                    }
                  }}
                />
              </div>

              {/* Asset Management Content */}
              <div className="p-6">
                <ProjectAssetManagement
                  project={selectedProject}
                  onEquipmentUpdate={() => {
                    refreshProjects();
                    triggerProgressRefresh();
                  }}
                />
              </div>
            </>
          )}
        </div>
      </RightDrawer>


      {/* Project Bulk Upload Modal */}
      <ProjectBulkUploadModal
        open={openBulkUploadDrawer}
        onClose={() => setOpenBulkUploadDrawer(false)}
        onSuccess={() => {
          setOpenBulkUploadDrawer(false);
          refreshProjects();
        }}
      />
    </div>
  );
}

function EquipmentFormWrapper({ projectId, onCreated }: { projectId: string; onCreated: () => void }) {
  // Lazy load to avoid circular import warnings
  const [Comp, setComp] = useState<React.ComponentType<any> | null>(null);
  useEffect(() => {
    import('../../components/EquipmentForm').then((m) => setComp(() => m.default));
  }, []);
  if (!Comp) return null;
  return <Comp projectId={projectId} onCreated={onCreated} />;
}